import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { liabilitiesAPI } from '../../data/liabilities';
import { unwrap } from '../../data/api';
import { invalidateGuidedFlow } from '../guidedFlow/queries';

export const liabilitiesKeys = {
  all: ['liabilities'],
  list: (params) => [...liabilitiesKeys.all, 'list', params || {}],
  detail: (id) => [...liabilitiesKeys.all, 'detail', id],
  repaymentPlan: (method) => [...liabilitiesKeys.all, 'repayment-plan', method],
  activePlan: () => [...liabilitiesKeys.all, 'active-plan'],
};


export function useLiabilitiesQuery(params, queryOptions = {}) {
  return useQuery({
    queryKey: liabilitiesKeys.list(params),
    queryFn: () => liabilitiesAPI.list(params).then((r) => unwrap(r, [])),
    ...queryOptions,
  });
}

export function useLiabilityQuery(id, queryOptions = {}) {
  return useQuery({
    queryKey: liabilitiesKeys.detail(id),
    queryFn: () => liabilitiesAPI.retrieve(id).then((r) => unwrap(r)),
    enabled: Boolean(id),
    ...queryOptions,
  });
}

export function useRepaymentPlanQuery(method, queryOptions = {}) {
  return useQuery({
    queryKey: liabilitiesKeys.repaymentPlan(method),
    queryFn: () => liabilitiesAPI.getRepaymentPlan(method).then((r) => unwrap(r)),
    ...queryOptions,
  });
}

export function useCreateLiabilityMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => liabilitiesAPI.create(payload).then((r) => unwrap(r)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: liabilitiesKeys.all, exact: false });
      invalidateGuidedFlow(qc);
    },
  });
}

export function useUpdateLiabilityMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) =>
      liabilitiesAPI.update(id, payload).then((r) => unwrap(r)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: liabilitiesKeys.all, exact: false });
      invalidateGuidedFlow(qc);
    },
  });
}

export function useEvaluateDebtMutation() {
  return useMutation({
    mutationFn: (payload) => liabilitiesAPI.evaluateNew(payload).then((r) => unwrap(r)),
  });
}

export function useSimulateExtraMutation() {
  return useMutation({
    mutationFn: ({ id, payload }) =>
      liabilitiesAPI.simulateExtra(id, payload).then((r) => unwrap(r)),
  });
}

export function useActiveRepaymentPlanQuery(queryOptions = {}) {
  return useQuery({
    queryKey: liabilitiesKeys.activePlan(),
    queryFn: () => liabilitiesAPI.getActivePlan().then((r) => unwrap(r)),
    ...queryOptions,
  });
}

export function useSaveRepaymentPlanMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => liabilitiesAPI.savePlan(payload).then((r) => unwrap(r)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: liabilitiesKeys.all, exact: false });
      invalidateGuidedFlow(qc);
    },
  });
}
